import { z } from "zod";
import { ImplementationType } from "../../models/OrganizationConfiguration/ImplementationType";

const folderPathSchema = z.object({
  folderPath: z
    .string()
    .optional()
    .refine(
      (v) => !!v?.trim() && v.trim().length > 1,
      "Folder path is required (minimum 2 characters)"
    ),
});

const folderArraySchema = z.array(folderPathSchema).nullable().optional();

// Drive/DocType mappings: both fields required. Two refines with explicit
// `path` so each error lands on its specific empty field — lets the row's
// `<FieldError name=".mapFrom"/>` + `<FieldError name=".mapTo"/>` render
// inline exactly where the user needs to look. A single object-level refine
// attaches its error to the row root instead, which no FieldError component
// watches, so the message renders blank.
const driveMappingSchema = z
  .object({
    mapFrom: z.string().optional(),
    mapTo: z.string().optional(),
  })
  .refine((m) => !!m.mapFrom?.trim(), {
    message: "Map From is required",
    path: ["mapFrom"],
  })
  .refine((m) => !!m.mapTo?.trim(), {
    message: "Map To is required",
    path: ["mapTo"],
  });

const docTypeMappingSchema = z
  .object({
    mapFrom: z.string().optional(),
    mapTo: z.string().optional(),
  })
  .refine((m) => !!m.mapFrom?.trim(), {
    message: "Map From is required",
    path: ["mapFrom"],
  })
  .refine((m) => !!m.mapTo?.trim(), {
    message: "Map To is required",
    path: ["mapTo"],
  });

const runtimeScheduleSchema = z.object({
  startLocalTime: z.string().min(1, "Start time is required"),
  endLocalTime: z.string().min(1, "End time is required"),
  scheduleType: z.number(),
});

const nullablePositiveInt = z
  .number()
  .int()
  .gt(0, "Must be greater than 0")
  .nullable()
  .optional();

/**
 * Creates a Zod schema for the ClientConfiguration form based on the organization's implementation type.
 * Fields like Filevine/Dropbox credentials are only required when the matching implementation type is active.
 *
 * Mirrors backend CreateClientConfigurationCommandValidator / UpdateClientConfigurationCommandValidator:
 * - Name: NotEmpty, not whitespace
 * - DriveMappings: both mapFrom and mapTo, or neither
 * - RootCaseFolders / RootProviderFolders: path empty or length > 1
 * - MaxConcurrentUploads: 1-64 when present
 * - MaxConcurrentDiscoveries: 1-1024 when present
 * - SourceApiMaxRequestsPer*: > 0 when present
 * - SharePoint config required when any migration enabled or uploadEmptyFoldersToSharepoint
 */
export const createClientConfigurationSchema = (implementationType?: ImplementationType) => {
  const isFilevine = implementationType === ImplementationType.Filevine;
  const isDropbox = implementationType === ImplementationType.Dropbox;
  const isAzureBlob = implementationType === ImplementationType.AzureBlob;
  const isDocsToNeos = implementationType === ImplementationType.DocsToNeos;
  // Sections that only render for non-API implementations
  const hasFolders = !isFilevine && !isDropbox;
  const hasMappings = hasFolders && !isDocsToNeos;

  return z
  .object({
    // Metadata (read-only, may be undefined for new configs)
    id: z.string().nullable().optional(),
    dateCreated: z.string().optional(),
    dateModified: z.string().optional(),
    modifiedBy: z.string().optional(),
    modifiedByUsername: z.string().optional(),
    createdBy: z.string().optional(),
    createdByUsername: z.string().optional(),
    isDefault: z.boolean().optional(),
    assignedClientsCount: z.number().optional(),

    // Name - required, non-whitespace. `preprocess(v => v ?? '')` + `.min(1)`
    // handles missing/null/undefined/empty-string uniformly; `.refine` adds the
    // whitespace-only guard. See filevine/dropbox below for the rationale.
    name: z.preprocess(
      (v) => v ?? "",
      z
        .string()
        .min(1, "Configuration name is required")
        .refine((v) => v.trim() !== "", "Configuration name cannot be empty or whitespace")
    ),

    // Database
    dbConnectionString: z.string().nullable().optional(),
    serviceVersion: z.string().nullable().optional(),

    // SharePoint
    sharepointClientId: z.string().nullable().optional(),
    sharepointTenantId: z.string().nullable().optional(),
    sharepointClientSecret: z.string().nullable().optional(),
    sharepointAppName: z.string().nullable().optional(),
    sharepointRootSiteName: z.string().nullable().optional(),
    sharepointRefreshToken: z.string().nullable().optional(),
    sharepointRefreshTokenId: z.string().nullable().optional(),
    casesSharepointRootDriveName: z.string().nullable().optional(),
    providersSharepointRootDriveName: z.string().nullable().optional(),
    templateFilesSharepointRootDriveName: z.string().nullable().optional(),
    firmDocumentsSharepointRootDriveName: z.string().nullable().optional(),
    // Comma-separated GraphLibraryType names. Declared rather than left to the schema's trailing
    // passthrough, so removing that passthrough cannot silently stop the field being submitted.
    versioningEnforcementOverrides: z.string().max(200).nullable().optional(),

    // Migration toggles
    migrateCases: z.boolean().default(false),
    migrateProviders: z.boolean().default(false),
    migrateTemplateFiles: z.boolean().default(false),
    migrateFirmDocuments: z.boolean().default(false),
    migrateIntakes: z.boolean().default(false),

    // Discovery toggles
    discoverCases: z.boolean().default(false),
    discoverProviders: z.boolean().default(false),
    discoverTemplateFiles: z.boolean().default(false),
    discoverFirmDocuments: z.boolean().default(false),
    discoverIntakes: z.boolean().default(false),

    // Upload toggles
    uploadStarterTemplates: z.boolean().default(false),
    uploadFilesMetadataToSharepoint: z.boolean().default(false),
    uploadEmptyFoldersToSharepoint: z.boolean().default(false),
    useNeedlesCategoryFolders: z.boolean().default(false),
    sendDebugLogs: z.boolean().default(false),

    // SharePoint upload tuning — mirror server's UpdateClientConfigurationCommandValidator.
    // Null = use client default. Empty/blank in the UI must round-trip as null, not 0.
    smallFilePutThresholdBytes: z
      .number()
      .int()
      .gt(0, "Must be > 0")
      .max(250_000_000, "Must be ≤ 250,000,000 (Microsoft Graph hard cap)")
      .nullable()
      .optional(),
    uploadSessionSliceSizeBytes: z
      .number()
      .int()
      .min(327_680, "Must be ≥ 327,680 (320 KiB)")
      .max(62_914_560, "Must be ≤ 62,914,560 (60 MiB)")
      .refine((v) => v % 327_680 === 0, "Must be a multiple of 327,680 (320 KiB)")
      .nullable()
      .optional(),
    enablePutTimeoutFallback: z.boolean().nullable().optional(),

    // Concurrency
    maxConcurrentUploads: z
      .number()
      .int()
      .min(1, "Maximum concurrent uploads must be between 1 and 64")
      .max(64, "Maximum concurrent uploads must be between 1 and 64")
      .nullable()
      .optional(),
    maxConcurrentDiscoveries: z
      .number()
      .int()
      .min(1, "Maximum concurrent discoveries must be between 1 and 1024")
      .max(1024, "Maximum concurrent discoveries must be between 1 and 1024")
      .nullable()
      .optional(),

    // API rate limits - only rendered for Filevine/Dropbox
    sourceApiMaxRequestsPerSecond: (isFilevine || isDropbox) ? nullablePositiveInt : z.any().optional(),
    sourceApiMaxRequestsPerHour: (isFilevine || isDropbox) ? nullablePositiveInt : z.any().optional(),
    sourceApiMaxRequestsPerDay: (isFilevine || isDropbox) ? nullablePositiveInt : z.any().optional(),

    // Filevine - required only when implementationType is Filevine.
    // In zod v4 the pattern `z.preprocess(v => v ?? '', z.string().min(1, ...))` is
    // what makes "required with custom message" work end-to-end:
    //   - missing key: preprocess coerces undefined to "", .min(1) catches it
    //   - undefined / null: same (v ?? '')
    //   - empty string: .min(1) catches it
    //   - non-empty string: passes
    // Using `.nullish().refine(...)` silently skips the check when the object key
    // is absent; using bare `z.string({ error }).min(1)` lets field-level type
    // errors short-circuit the root-level `.check()` so cross-field validation
    // (SharePoint auth when migration enabled, AzureBlob fields, etc.) stops running.
    filevinePersonalAccessToken: isFilevine
      ? z.preprocess((v) => v ?? "", z.string().min(1, "Personal Access Token is required"))
      : z.string().optional(),
    filevineClientId: isFilevine
      ? z.preprocess((v) => v ?? "", z.string().min(1, "Client ID is required"))
      : z.string().optional(),
    filevineClientSecret: isFilevine
      ? z.preprocess((v) => v ?? "", z.string().min(1, "Client Secret is required"))
      : z.string().optional(),
    filevineUseBatchEndpoint: z.boolean().optional(),

    // Dropbox - required only when implementationType is Dropbox (see Filevine note above)
    dropboxApiId: isDropbox
      ? z.preprocess((v) => v ?? "", z.string().min(1, "API ID is required"))
      : z.string().optional(),
    dropboxAppSecret: isDropbox
      ? z.preprocess((v) => v ?? "", z.string().min(1, "App Secret is required"))
      : z.string().optional(),
    dropboxRefreshToken: isDropbox
      ? z.preprocess((v) => v ?? "", z.string().min(1, "Refresh Token is required"))
      : z.string().optional(),
    dropboxRefreshTokenId: z.string().nullable().optional(),
    dropboxAccountType: z.string().nullable().optional(),

    // Collections - conditionally validated based on implementation type

    // Azure Blob - validated in superRefine when AzureBlob
    azureBlobs: z.array(z.object({
      connectionString: z.string().nullish(),
      blobName: z.string().nullish(),
      csvConfigurationPath: z.string().nullish(),
    })).nullable().optional(),

    // Folder configs - only rendered when NOT Filevine/Dropbox
    firmDocumentFolders: hasFolders ? folderArraySchema : z.any().optional(),
    rootCaseFolders: hasFolders ? folderArraySchema : z.any().optional(),
    rootProviderFolders: hasFolders ? folderArraySchema : z.any().optional(),

    // Dropbox root folders - only rendered when Dropbox. Folder paths must be
    // either empty or longer than 1 character (matches rootCaseFolders rule).
    dropboxRootFolders: isDropbox
      ? z.array(z.object({
          folderPath: z
            .string()
            .optional()
            .refine(
              (v) => !!v?.trim() && v.trim().length > 1,
              "Folder path is required (minimum 2 characters)"
            ),
          migrationType: z.string(),
        })).nullable().optional()
      : z.any().optional(),
    runtimeSchedules: z.array(runtimeScheduleSchema).nullable().optional(),
    // Drive mappings - only rendered when NOT Filevine/Dropbox/DocsToNeos
    driveMappings: hasMappings ? z.array(driveMappingSchema).nullable().optional() : z.any().optional(),
    // Doc type mappings - only rendered for TrialWorks (within mappings section)
    docTypeMappings: z.array(docTypeMappingSchema).nullable().optional(),
    flags: z
      .array(
        z.object({
          flagType: z.string(),
          flagProperties: z
            .array(z.object({ key: z.string(), value: z.string() }))
            .optional(),
        })
      )
      .nullable()
      .optional(),
    fileTypeFilters: z
      .array(z.object({ fileExtension: z.string().optional() }))
      .nullable()
      .optional(),
  })
  .passthrough()
  // Use `.check()` instead of `.superRefine()` — in zod v4, `.superRefine()`
  // is skipped when field-level parses fail, which would hide cross-field
  // errors like "Connection string required". `.check()` runs regardless.
  .check((ctx) => {
    const data = ctx.value;
    const addIssue = (path: (string | number)[], message: string) =>
      ctx.issues.push({ code: "custom", path, message, input: data });

    // Database connection string required for implementations backed by a DB
    // (Needles 4/5, TrialWorks). The UI hides the field for
    // DocsToNeos/Filevine/Dropbox/AzureBlob — see ClientSettingsSection.showDbField.
    const dbRequired = !isFilevine && !isDropbox && !isDocsToNeos && !isAzureBlob;
    if (dbRequired && (data.dbConnectionString == null || data.dbConnectionString.trim() === "")) {
      addIssue(["dbConnectionString"], "Database connection string is required");
    }

    // Azure Blob validation - connectionString, blobName, and csvConfigurationPath
    // all required when AzureBlob
    if (isAzureBlob) {
      const blob = data.azureBlobs?.at(0);
      if (!blob?.connectionString?.trim()) {
        addIssue(["azureBlobs", 0, "connectionString"], "Connection string is required");
      }
      if (!blob?.blobName?.trim()) {
        addIssue(["azureBlobs", 0, "blobName"], "Blob name is required");
      }
      if (!blob?.csvConfigurationPath?.trim()) {
        addIssue(["azureBlobs", 0, "csvConfigurationPath"], "CSV configuration path is required");
      }
    }

    // When a named SharePoint app is selected (not "Custom"), the server
    // resolves clientId/clientSecret from the app registration — they don't
    // need to be present on the client-side form.
    const usesNamedApp =
      data.sharepointAppName != null &&
      data.sharepointAppName.trim() !== "" &&
      data.sharepointAppName.trim() !== "Custom";
    const usesCustomApp = data.sharepointAppName?.trim() === "Custom";

    const hasClientId =
      data.sharepointClientId != null && data.sharepointClientId.trim() !== "";
    const hasClientSecret =
      data.sharepointClientSecret != null && data.sharepointClientSecret.trim() !== "";

    // Custom app credentials are required whenever Custom is selected — even
    // when no migration toggle is on yet — because the server's
    // SharepointAppsConfiguration.TryApplySharepointAppSelection rejects a
    // Custom-app save without manual ClientId+ClientSecret regardless of
    // migration state. Surfacing the rule client-side gives the user inline
    // FieldError feedback instead of a generic backend toast.
    if (usesCustomApp && !hasClientId) {
      addIssue(["sharepointClientId"], "SharePoint Client ID is required for Custom app");
    }
    if (usesCustomApp && !hasClientSecret) {
      addIssue(["sharepointClientSecret"], "SharePoint Client Secret is required for Custom app");
    }

    const isMigrationEnabled =
      data.migrateCases ||
      data.migrateIntakes ||
      data.migrateProviders ||
      data.migrateFirmDocuments ||
      data.migrateTemplateFiles;

    if (!isMigrationEnabled && !data.uploadEmptyFoldersToSharepoint) {
      return;
    }

    const hasAuthInfo =
      (data.sharepointTenantId != null && data.sharepointTenantId.trim() !== "") ||
      (data.sharepointRefreshToken != null && data.sharepointRefreshToken.trim() !== "");

    if (!hasAuthInfo) {
      addIssue(["sharepointTenantId"], "SharePoint Tenant ID or Refresh Token is required when migration is enabled");
    }

    if (!usesNamedApp && !hasClientId) {
      addIssue(["sharepointClientId"], "SharePoint Client ID is required when migration is enabled");
    }

    if (!usesNamedApp && !hasClientSecret) {
      addIssue(["sharepointClientSecret"], "SharePoint Client Secret is required when migration is enabled");
    }
  });
};
